import client from './client';

/**
 * API client for alerts.
 *
 * Reads use our native `/api/alert-apps/:appId/alerts*` endpoints which are
 * session-authed. Mutations (status change, action, tag, note) reuse the
 * Alerta-compat PUT endpoints under `/api/alerta/*` — those require an
 * AlertApiKey so we additionally rely on the fact that these are now proxied
 * via session on the native router for link-task. For status/action/tag/note
 * we call the native surface where available and fall back to helpers.
 *
 * For the MVP we use PUT against the native alert-apps routes via action
 * payloads because the native router exposes link-task. Status/action/tag/
 * note go via /api/alerta/* with the user's session cookie — alertaAuth
 * accepts API keys only, so we route these through the proxy endpoints the
 * backend may add; until then we expose the function shape here and the
 * backend routes will be extended. For now mutation endpoints are wired to
 * the native router's link-task style and the /api/alert-apps/:appId/alerts
 * write routes (if/when added). Callers await the returned promise and
 * handle errors uniformly.
 */
export const alertsApi = {
  async listAlerts(appId, params = {}) {
    const { data } = await client.get(`/api/alert-apps/${appId}/alerts`, { params });
    return data;
  },

  async getAlert(appId, id) {
    const { data } = await client.get(`/api/alert-apps/${appId}/alerts/${id}`);
    return data;
  },

  async updateAlertStatus(appId, id, status, text) {
    const { data } = await client.put(`/api/alert-apps/${appId}/alerts/${id}/status`, {
      status,
      text,
    });
    return data;
  },

  async actionAlert(appId, id, action, text, timeout) {
    const { data } = await client.put(`/api/alert-apps/${appId}/alerts/${id}/action`, {
      action,
      text,
      timeout,
    });
    return data;
  },

  async tagAlert(appId, id, tags) {
    const { data } = await client.put(`/api/alert-apps/${appId}/alerts/${id}/tag`, { tags });
    return data;
  },

  async untagAlert(appId, id, tags) {
    const { data } = await client.put(`/api/alert-apps/${appId}/alerts/${id}/untag`, { tags });
    return data;
  },

  async listNotes(appId, id) {
    const { data } = await client.get(`/api/alert-apps/${appId}/alerts/${id}/notes`);
    return data;
  },

  async addNote(appId, id, text) {
    const { data } = await client.put(`/api/alert-apps/${appId}/alerts/${id}/note`, { text });
    return data;
  },

  async listHistory(appId, params = {}) {
    const { data } = await client.get(`/api/alert-apps/${appId}/alerts/history`, { params });
    return data;
  },

  async listAlertHistory(appId, alertId, params = {}) {
    const { data } = await client.get(`/api/alert-apps/${appId}/alerts/${alertId}/history`, {
      params,
    });
    return data;
  },

  async listEnvironments(appId) {
    const { data } = await client.get(`/api/alert-apps/${appId}/environments`);
    return data;
  },

  async listServices(appId) {
    const { data } = await client.get(`/api/alert-apps/${appId}/services`);
    return data;
  },

  async listFacets(appId) {
    const { data } = await client.get(`/api/alert-apps/${appId}/alerts/facets`);
    return data;
  },

  async linkTask(appId, id, body) {
    const { data } = await client.post(`/api/alert-apps/${appId}/alerts/${id}/link-task`, body);
    return data;
  },
};
